import CustomToast from '../../../../../components/Toast';
import PageSize from '../../../../../enums/pageSize';
import { RANKING_TYPES, getRankingRows, getName, getMetric, formatMetric } from './rankingTableHelpers';

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;'
}[char]));

export const printPdfReport = async ({ reportData, printPDF, ferpaEnabled = true }) => {
  try {
    if (!reportData) {
      CustomToast('No report data to print', 'error');
      return;
    }

    const contentWrapper = document.getElementById('reportWrapper');
    if (!contentWrapper) {
      CustomToast('Report content not found', 'error');
      return;
    }

    const htmlString = contentWrapper.innerHTML;
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const measureRenderedHeight = (selector, fallbackHeight) => {
      const renderedElement = contentWrapper.querySelector(selector);
      const renderedHeight = renderedElement ? Math.round(renderedElement.getBoundingClientRect().height) : 0;

      return renderedHeight > 0 ? renderedHeight : fallbackHeight;
    };

    const pdfFontStyle = document.createElement('style');

    pdfFontStyle.textContent =
      `* {
          font-family: 'Poppins', sans-serif !important;
        }

        body, div, span, p, h1, h2, h3, h4, h5, h6, label, table, th, 
        td {
          font-family: 'Poppins', sans-serif !important;
        }`;

    tempContainer.prepend(pdfFontStyle);

    const buttonElements = tempContainer.querySelectorAll('button');
    buttonElements.forEach((btn) => {
      btn.classList.add('d-none');
    });

    const rankingCard = tempContainer.querySelector('.companion-ranking-card');
    if (rankingCard) {
      const rankings = reportData?.topUsageRanking ?? reportData?.topUsageRankings ?? {
        districts: reportData?.topDistricts ?? reportData?.districtRankings ?? [],
        schools: reportData?.topSchools ?? reportData?.schoolRankings ?? [],
        students: reportData?.topStudents ?? reportData?.studentRankings ?? []
      };

      const rankingTypesWithData = Object.keys(RANKING_TYPES).filter(
        (type) => getRankingRows(rankings, type).length > 0
      );

      const rankingTypesToRender = rankingTypesWithData.length > 0 ? rankingTypesWithData : ['districts'];

      const buildRankingCardForType = (type) => {
        const card = rankingCard.cloneNode(true);
        const config = RANKING_TYPES[type];
        const rows = getRankingRows(rankings, type).slice(0, 10);

        const nameHeaderCell = card.querySelector('.companion-ranking-table thead th:nth-child(2)');
        if (nameHeaderCell) {
          nameHeaderCell.textContent = config.nameHeader;
        }

        const rankingTabButton = Array.from(card.querySelectorAll('.companion-ranking-tabs button'))
          .find((btn) => btn.textContent.trim() === config.label);
        rankingTabButton?.classList.remove('d-none');
        rankingTabButton?.classList.add('active', 'mx-2');

        const description = card.querySelector('.companion-ranking-description');
        if (description) {
          description.textContent = `Showing the top 10 ${type} on the platform. Export to Excel for the full list.`;
        }

        const tbody = card.querySelector('.companion-ranking-table tbody');
        if (tbody) {
          tbody.innerHTML = rows.length === 0
            ? '<tr><td class="companion-empty-state" colspan="5">No ranking data available.</td></tr>'
            : rows.map((row, index) => `
              <tr>
                <td>${escapeHtml(row.rank ?? index + 1)}</td>
                <td>${escapeHtml(getName(row, type, ferpaEnabled))}</td>
                <td>${escapeHtml(formatMetric(getMetric(row, 'sessionsPerUser', 'sessionsPerUserCount')))}</td>
                <td>${escapeHtml(formatMetric(getMetric(row, 'averageSessionLength', 'avgSessionMinutes', 'averageSessionMinutes')))}</td>
                <td>${escapeHtml(formatMetric(getMetric(row, 'engagementScore')))}</td>
              </tr>
            `).join('');
        }

        card.classList.add('mb-4');
        return card;
      };

      const rankingCardsFragment = document.createDocumentFragment();
      rankingTypesToRender.forEach((type) => {
        const card = buildRankingCardForType(type);
        card.style.pageBreakBefore = 'always';
        card.style.breakBefore = 'page';
        rankingCardsFragment.appendChild(card);
      });

      rankingCard.replaceWith(rankingCardsFragment);
    }

    const ferpaElements = tempContainer.querySelectorAll('.ferpaContainer');
    ferpaElements.forEach((el) => {
      el.classList.add('d-none');
    });

    const heading = tempContainer.querySelector('h1');
    if (heading) {
      heading.style.textAlign = 'center';
      heading.style.fontWeight = '700';
      heading.style.width = '100%';
    }

    const metricRow = tempContainer.querySelector('.metric-cards-row');

    if (metricRow) {
      metricRow.style.display = 'flex';
      metricRow.style.flexWrap = 'nowrap';
      metricRow.style.width = '100%';

      Array.from(metricRow.children).forEach((col) => {
        col.style.flex = '1 1 0';
        col.style.width = '25%';
        col.style.maxWidth = '25%';
        col.style.minWidth = '0';
      });
    }

    const chartPlot = tempContainer.querySelector(
      '.feature-usage-chart__plot'
    );

    const featureUsageContent = tempContainer.querySelector(
      '.feature-usage-chart__content'
    );

    const chartBars = tempContainer.querySelector(
      '.feature-usage-chart__bars'
    );

    const chartYAxis = tempContainer.querySelector(
      '.feature-usage-chart__y-axis'
    );

    const chartHeight = measureRenderedHeight('.feature-usage-chart__plot', 380);

    if (chartPlot) {
      chartPlot.style.position = 'relative';
      chartPlot.style.height = `${chartHeight}px`;
      chartPlot.style.minHeight = `${chartHeight}px`;
      chartPlot.style.overflow = 'visible';
    }

    if (featureUsageContent) {
      featureUsageContent.style.margin = '0';
    }

    if (chartBars) {
      chartBars.style.height = `${chartHeight}px`;
      chartBars.style.minHeight = `${chartHeight}px`;
      chartBars.style.position = 'absolute';
      chartBars.style.left = '0';
      chartBars.style.right = '0';
      chartBars.style.bottom = '0';

      chartBars.querySelectorAll(
        '.feature-usage-chart__bar, .feature-usage-chart__loading-bar'
      ).forEach((bar) => {
        bar.style.maxWidth = '44px';
        bar.style.marginLeft = '6px';
        bar.style.marginRight = '6px';
      });
    }

    if (chartYAxis) {
      chartYAxis.style.position = 'relative';
      chartYAxis.style.height = `${chartHeight}px`;
      chartYAxis.style.minHeight = `${chartHeight}px`;

      const yAxisLabels = chartYAxis.querySelectorAll(
        '.feature-usage-chart__y-axis-label'
      );

      yAxisLabels.forEach((label) => {
        const topPercent = parseFloat(label.style.top);

        if (Number.isNaN(topPercent)) {
          return;
        }

        label.style.position = 'absolute';
        label.style.top = `${
          (topPercent / 100) * chartHeight
        }px`;

        label.style.whiteSpace = 'nowrap';
        label.style.lineHeight = '1';

        if (topPercent === 0) {
          label.style.transform = 'translateY(0)';
        } else if (topPercent === 100) {
          label.style.transform = 'translateY(-100%)';
        } else {
          label.style.transform = 'translateY(-50%)';
        }
      });
    }

    const chartXAxis = tempContainer.querySelector(
      '.feature-usage-chart__x-axis'
    );

    if (chartXAxis) {
      chartXAxis.style.position = 'relative';
      chartXAxis.style.top = 'auto';
      chartXAxis.style.bottom = 'auto';
      chartXAxis.style.margin = '10px 0 0';

      const chartXAxisSpacer = chartXAxis.querySelector(
        '.feature-usage-chart__x-axis-spacer'
      );
      const chartXAxisLabels = chartXAxis.querySelector(
        '.feature-usage-chart__x-axis-labels'
      );

      if (chartXAxisSpacer && chartYAxis) {
        const yAxisWidth = chartYAxis.style.width || '52px';
        chartXAxisSpacer.style.width = `${yAxisWidth}px`;
      }

      if (chartXAxisLabels) {
        chartXAxisLabels.style.flex = '1 1 auto';
      }
    }

    const retentionSummaryGrid = tempContainer.querySelector(
      '.companion-retention-summary-grid'
    );

    if (retentionSummaryGrid) {
      retentionSummaryGrid.style.display = 'flex';
      retentionSummaryGrid.style.gap = '0';

      Array.from(retentionSummaryGrid.children).forEach((metric, index, metrics) => {
        metric.style.flex = '1 1 0';
        metric.style.minWidth = '0';
        if (index < metrics.length - 1) {
          metric.style.marginRight = '12px';
        }
      });
    }

    const retentionChartWrap = tempContainer.querySelector(
      '.companion-chart-wrap'
    );

    if (retentionChartWrap) {
      retentionChartWrap.style.height = '260px';

      const retentionChartSvg = retentionChartWrap.querySelector('svg');
      if (retentionChartSvg) {
        retentionChartSvg.style.width = '100%';
        retentionChartSvg.style.height = '260px';
        retentionChartSvg.removeAttribute('preserveAspectRatio');
      }
    }

    const retentionLegend = tempContainer.querySelector(
      '.companion-chart-legend'
    );

    if (retentionLegend) {
      retentionLegend.style.gap = '0';

      retentionLegend.querySelectorAll(':scope > span').forEach((item, index, items) => {
        if (index < items.length - 1) {
          item.style.marginRight = '14px';
        }
      });
    }

    tempContainer.querySelectorAll('.companion-report-card').forEach(card => {
      if (
        card.querySelector('.active-user-trend__chart') ||
        card.querySelector('.feature-usage-chart__content') ||
        card.querySelector('.companion-retention-summary-grid') ||
        card.classList.contains('companion-ranking-card')
      ) {
        card.style.height = 'auto';
        card.style.maxHeight = 'none';
        card.style.overflow = 'visible';
        return;
      }

      card.style.height = '440px';
      card.style.maxHeight = '440px';
      card.style.overflow = 'hidden';
    });

    const svgElements = tempContainer.querySelectorAll('svg.svg-inline--fa.fa-circle-question');
    svgElements.forEach((svgElement) => {
      svgElement.classList.add('d-none');
    });

    const PDF_CHART_HEIGHT = measureRenderedHeight('.active-user-trend__chart .recharts-responsive-container', 400);
    const PDF_PRINTABLE_WIDTH = 1050;
    const PDF_CHART_FIT_RATIO = 0.96;

    const chart = tempContainer.querySelector(
      '.active-user-trend__chart'
    );

    if (chart) {
      const responsive = chart.querySelector(
        '.recharts-responsive-container'
      );

      const wrapper = chart.querySelector(
        '.recharts-wrapper'
      );

      const renderedWrapper = contentWrapper.querySelector(
        '.active-user-trend__chart .recharts-wrapper'
      );

      const renderedWidth = Math.round(renderedWrapper?.getBoundingClientRect().width || 0);
      const renderedHeight = Math.round(renderedWrapper?.getBoundingClientRect().height || PDF_CHART_HEIGHT);

      const reportWidth = Math.round(contentWrapper.getBoundingClientRect().width || 0);
      const pageScale = reportWidth > PDF_PRINTABLE_WIDTH ? PDF_PRINTABLE_WIDTH / reportWidth : 1;
      const scale = pageScale * PDF_CHART_FIT_RATIO;

      if (wrapper && renderedWidth > 0) {
        wrapper.style.width = `${renderedWidth}px`;
        wrapper.style.height = `${renderedHeight}px`;
        wrapper.style.transform = `scale(${scale})`;
        wrapper.style.transformOrigin = 'top left';

        // Hairline dashed grid lines drop out once scaled down, so thicken and re-space them.
        wrapper.querySelectorAll('.recharts-cartesian-grid line').forEach((gridLine) => {
          const dashLength = (3 / scale).toFixed(2);

          gridLine.setAttribute('stroke', '#dddddd');
          gridLine.setAttribute('stroke-width', (1 / scale).toFixed(2));
          gridLine.setAttribute('stroke-dasharray', `${dashLength} ${dashLength}`);
          gridLine.setAttribute('shape-rendering', 'crispEdges');
        });

        // The PDF renderer ignores flex `gap`, so spacing has to come from margins, sized up for the scale.
        const legendRow = wrapper.querySelector('.recharts-legend-wrapper > div');

        if (legendRow) {
          legendRow.style.gap = '0';

          legendRow.querySelectorAll(':scope > div').forEach((legendItem) => {
            legendItem.style.gap = '0';
            legendItem.style.margin = `0 ${(8 / scale).toFixed(2)}px`;

            const swatch = legendItem.querySelector('svg');

            if (swatch) {
              swatch.style.marginRight = `${(6 / scale).toFixed(2)}px`;
            }
          });
        }
      }

      if (responsive && renderedWidth > 0) {
        responsive.style.width = `${Math.ceil(renderedWidth * scale)}px`;
        responsive.style.height = `${Math.ceil(renderedHeight * scale)}px`;
        responsive.style.maxWidth = '100%';
        responsive.style.display = 'block';
        responsive.style.margin = '0 auto';
        responsive.style.overflow = 'visible';
      }
    }

    const icons = tempContainer.querySelectorAll(
      'svg.svg-inline--fa'
    );

    icons.forEach((icon) => {
      icon.style.width = '1em';
      icon.style.height = '1em';
      icon.style.maxWidth = '1em';
      icon.style.maxHeight = '1em';
    });

    const pageBreakElements = tempContainer.querySelectorAll(
      '.page-break-avoid'
    );

    pageBreakElements.forEach((el) => {
      el.style.pageBreakInside = 'avoid';
      el.style.breakInside = 'avoid-page';
      el.style.display = 'block';
    });

    await printPDF('Companion_App_Usage_Report', tempContainer.innerHTML, PageSize.A4, true);
    CustomToast('Report printed successfully', 'success');
  } catch (error) {
    CustomToast(error?.message || 'Failed to generate PDF', 'error');
  }
};